import React, { createContext, useContext, useState } from 'react';
import { HubspotOpportunityWithCustomer } from '@/types/hubspot';

interface Customer {
  id: string;
  name: string;
  legal_name: string;
}

interface CustomerRelationship {
  id: string;
  customer_id: string;
  child_id: string;
  relationship?: string;
  childName?: string;
}

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  is_active: boolean;
  billing_frequency?: string;
  family_id?: string;
  sku?: string;
  billing_period_type?: string;
  billing_period_duration?: number;
  description?: string;
  prices?: SubscriptionPlanPrice[];
}

interface CustomerContact {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
}

interface SelectedContact {
  contact_id: string;
  contact_type: 'technical' | 'administrative' | 'billing' | 'pedagogical';
  contact: CustomerContact;
}

export interface SelectedPlan {
  subscription_plan_id: string;
  subscription_plan: any; // Store the full plan object
  quantity: number;
  unit_price: number;
  current_term_start: string;
  current_term_end: string;
  billing_start_terms?: 'immediately' | 'specific-date';
  auto_renew?: boolean;
  billing_cycles?: number;
  billing_by?: string;
  term_start?: string;
  term_end?: string;
  needsReview?: boolean;
  isBillingStartReadOnly?: boolean; // Add this line
  isAutoRenewReadOnly?: boolean; // Add this line
}

export interface ChildSubscriptionConfig {
  id: string;
  name: string;
  customer: Customer;
  participants: Customer[];
  planAllocations: { [planId: string]: number }; // planId -> quantity allocated
}

interface SubscriptionWizardState {
  // Step 1: Customer Owner
  selectedCustomer: Customer | null; // Este será o owner
  
  // Step 1b: HubSpot Opportunity
  selectedOpportunity: HubspotOpportunityWithCustomer | null;
  
  // Step 1c: Subscription Type
  subscriptionType: 'simple' | 'split' | null;
  
  // Step 2: Child Customers (for simple type)
  selectedChildCustomers: Customer[]; // Múltiplos children
  
  // Step 3: Contacts
  selectedContacts: SelectedContact[];
  
  // Step 4: Subscription Details
  startDate: string;
  currencyCode: string;
  poNumber: string;
  paymentSourceId: string;
  netTermDays: number;
  dueDateType: string;
  dueDateDay: number;
  dueDateNextMonth: boolean;
  
  // Step 5: Plans
  selectedPlans: SelectedPlan[];
  
  // Step 5b: Split Configuration (for split type)
  childSubscriptions: ChildSubscriptionConfig[];
}

interface SubscriptionWizardContextType {
  state: SubscriptionWizardState;
  setSelectedCustomer: (customer: Customer | null) => void;
  setSelectedOpportunity: (opportunity: HubspotOpportunityWithCustomer | null) => void;
  setSubscriptionType: (type: 'simple' | 'split' | null) => void;
  setSelectedChildCustomers: (customers: Customer[]) => void;
  setSubscriptionContacts: (contacts: SelectedContact[]) => void;
  setSubscriptionDetails: (details: {
    startDate?: string;
    currencyCode?: string;
    poNumber?: string;
    paymentSourceId?: string;
    netTermDays?: number;
    dueDateType?: string;
    dueDateDay?: number;
    dueDateNextMonth?: boolean;
  }) => void;
  addSelectedPlan: (plan: SelectedPlan) => void;
  updateSelectedPlan: (index: number, plan: Partial<SelectedPlan>) => void;
  removeSelectedPlan: (index: number) => void;
  setChildSubscriptions: (childSubs: ChildSubscriptionConfig[]) => void;
  addChildSubscription: (childSub: ChildSubscriptionConfig) => void;
  updateChildSubscription: (index: number, childSub: Partial<ChildSubscriptionConfig>) => void;
  removeChildSubscription: (index: number) => void;
  resetWizard: () => void;
}

const defaultState: SubscriptionWizardState = {
  selectedCustomer: null,
  selectedOpportunity: null,
  subscriptionType: null,
  selectedChildCustomers: [],
  selectedContacts: [],
  startDate: new Date().toISOString().split('T')[0],
  currencyCode: 'BRL',
  poNumber: '',
  paymentSourceId: '',
  netTermDays: 0,
  dueDateType: 'fixed_day',
  dueDateDay: 5,
  dueDateNextMonth: false,
  selectedPlans: [],
  childSubscriptions: [],
};

const SubscriptionWizardContext = createContext<SubscriptionWizardContextType | undefined>(undefined);

export const SubscriptionWizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SubscriptionWizardState>(defaultState);

  const setSelectedCustomer = (customer: Customer | null) => {
    setState((prev) => ({
      ...prev,
      selectedCustomer: customer,
      // Reset opportunity if customer changes
      selectedOpportunity: customer?.id === prev.selectedCustomer?.id ? prev.selectedOpportunity : null,
      // Reset relationship if customer changes
      selectedChildCustomers: customer?.id === prev.selectedCustomer?.id ? prev.selectedChildCustomers : [],
    }));
  };

  const setSelectedOpportunity = (opportunity: HubspotOpportunityWithCustomer | null) => {
    setState((prev) => ({
      ...prev,
      selectedOpportunity: opportunity,
    }));
  };

  const setSelectedChildCustomers = (customers: Customer[]) => {
    setState((prev) => ({
      ...prev,
      selectedChildCustomers: customers,
      // Reset contacts if child customers change
      selectedContacts: [],
    }));
  };

  const setSubscriptionContacts = (contacts: SelectedContact[]) => {
    setState((prev) => ({
      ...prev,
      selectedContacts: contacts,
    }));
  };

  const setSubscriptionDetails = (details: {
    startDate?: string;
    currencyCode?: string;
    poNumber?: string;
    paymentSourceId?: string;
    netTermDays?: number;
    dueDateType?: string;
    dueDateDay?: number;
    dueDateNextMonth?: boolean;
  }) => {
    setState((prev) => ({
      ...prev,
      ...details,
      // Reset selected plans if currency changes
      selectedPlans: details.currencyCode && details.currencyCode !== prev.currencyCode ? [] : prev.selectedPlans,
    }));
  };

  const addSelectedPlan = (plan: SelectedPlan) => {
    setState((prev) => {
      // Check if the plan (by subscription_plan_id) already exists
      const isDuplicate = prev.selectedPlans.some(
        existingPlan => existingPlan.subscription_plan_id === plan.subscription_plan_id
      );

      if (isDuplicate) {
        // If it's a duplicate, return the previous state without adding
        console.warn(`Attempted to add duplicate plan, ignoring: ${plan.subscription_plan?.name} (ID: ${plan.subscription_plan_id})`);
        // Optionally, show a toast message here if needed, though Step5Plans might handle it already
        // toast.warning('Plano duplicado não adicionado.'); 
        return prev; 
      }

      // If not a duplicate, add the new plan
      return {
        ...prev,
        selectedPlans: [...prev.selectedPlans, plan],
      };
    });
  };

  const updateSelectedPlan = (index: number, plan: Partial<SelectedPlan>) => {
    setState((prev) => {
      const updatedPlans = [...prev.selectedPlans];
      updatedPlans[index] = { ...updatedPlans[index], ...plan };
      return {
        ...prev,
        selectedPlans: updatedPlans,
      };
    });
  };

  const removeSelectedPlan = (index: number) => {
    setState((prev) => {
      const updatedPlans = [...prev.selectedPlans];
      updatedPlans.splice(index, 1);
      return {
        ...prev,
        selectedPlans: updatedPlans,
      };
    });
  };

  const setSubscriptionType = (type: 'simple' | 'split' | null) => {
    setState((prev) => ({
      ...prev,
      subscriptionType: type,
      // Reset child subscriptions when type changes
      childSubscriptions: type === 'split' ? prev.childSubscriptions : [],
    }));
  };

  const setChildSubscriptions = (childSubs: ChildSubscriptionConfig[]) => {
    setState((prev) => ({
      ...prev,
      childSubscriptions: childSubs,
    }));
  };

  const addChildSubscription = (childSub: ChildSubscriptionConfig) => {
    setState((prev) => ({
      ...prev,
      childSubscriptions: [...prev.childSubscriptions, childSub],
    }));
  };

  const updateChildSubscription = (index: number, childSub: Partial<ChildSubscriptionConfig>) => {
    setState((prev) => {
      const updatedChildSubs = [...prev.childSubscriptions];
      updatedChildSubs[index] = { ...updatedChildSubs[index], ...childSub };
      return {
        ...prev,
        childSubscriptions: updatedChildSubs,
      };
    });
  };

  const removeChildSubscription = (index: number) => {
    setState((prev) => {
      const updatedChildSubs = [...prev.childSubscriptions];
      updatedChildSubs.splice(index, 1);
      return {
        ...prev,
        childSubscriptions: updatedChildSubs,
      };
    });
  };

  const resetWizard = () => {
    setState(defaultState);
  };

  return (
    <SubscriptionWizardContext.Provider
      value={{
        state,
        setSelectedCustomer,
        setSelectedOpportunity,
        setSubscriptionType,
        setSelectedChildCustomers,
        setSubscriptionContacts,
        setSubscriptionDetails,
        addSelectedPlan,
        updateSelectedPlan,
        removeSelectedPlan,
        setChildSubscriptions,
        addChildSubscription,
        updateChildSubscription,
        removeChildSubscription,
        resetWizard,
      }}
    >
      {children}
    </SubscriptionWizardContext.Provider>
  );
};

export const useSubscriptionWizard = () => {
  const context = useContext(SubscriptionWizardContext);
  if (context === undefined) {
    throw new Error('useSubscriptionWizard must be used within a SubscriptionWizardProvider');
  }
  return context;
}; 